import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { logger } from '../../../shared/utils/logger';

export interface IFCModel {
  id: string;
  name: string;
  file: File | null;
  fingerprint?: string | null;
  storeys: any[];
  categories: any[];
  isLoaded: boolean;
  center: [number, number] | null;
  extent: [number, number, number, number] | null;
  // Añadir referencia a la instancia real del modelo
  ifcModelInstance?: any;
}

export interface IFCState {
  // Estado del servicio
  isInitialized: boolean;
  isReady: boolean;
  
  // Modelos IFC
  models: IFCModel[];
  currentModel: IFCModel | null;
  selectedModelId: string | null;
  
  // Plantas y categorías
  currentStorey: any | null;
  selectedCategories: string[];
  filteredCategories: string[];
  
  // Configuración de visualización
  showCategories: boolean;
  showStoreys: boolean;
  transparency: number;
  
  // Estado de visibilidad de niveles (persistente)
  storeyVisibility: Record<string, boolean>;
  
  // Operaciones
  isLoading: boolean;
  isUploading: boolean;
  error: string | null;
  
  // Acciones
  setInitialized: (initialized: boolean) => void;
  setReady: (ready: boolean) => void;
  addModel: (model: IFCModel) => void;
  removeModel: (modelId: string) => void;
  setCurrentModel: (model: IFCModel | null) => void;
  setSelectedModelId: (modelId: string | null) => void;
  updateModel: (modelId: string, updates: Partial<IFCModel>) => void;
  setCurrentStorey: (storey: any | null) => void;
  setSelectedCategories: (categories: string[]) => void;
  addSelectedCategory: (category: string) => void;
  removeSelectedCategory: (category: string) => void;
  setFilteredCategories: (categories: string[]) => void;
  updateAvailableCategoriesForStorey: (categories: string[]) => void;
  toggleShowCategories: () => void;
  toggleShowStoreys: () => void;
  setTransparency: (transparency: number) => void;
  setStoreyVisibility: (storeyId: string, visible: boolean) => void;
  setAllStoreysVisibility: (visible: boolean) => void;
  loadStoreyVisibilityFromStorage: () => void;
  saveStoreyVisibilityToStorage: () => void;
  setLoading: (loading: boolean) => void;
  setUploading: (uploading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  
  // Métodos para verificar el estado del modelo
  hasLoadedModel: () => boolean;
  getCurrentIfcModel: () => any;
  getModelById: (modelId: string) => IFCModel | null;
  getCurrentModelId: () => string | null;
  isModelVisible: (modelId: string) => boolean;
}

const initialState = {
  isInitialized: false,
  isReady: false,
  models: [],
  currentModel: null,
  selectedModelId: null,
  currentStorey: null,
  selectedCategories: [],
  filteredCategories: [],
  showCategories: true,
  showStoreys: true,
  transparency: 1.0,
  storeyVisibility: {},
  isLoading: false,
  isUploading: false,
  error: null,
};

export const useIFCStore = create<IFCState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      
      setInitialized: (initialized) => set({ isInitialized: initialized }),
      setReady: (ready) => set({ isReady: ready }),
      
      addModel: (model) => set((state) => ({
        models: [...state.models, model],
        currentModel: model,
        selectedModelId: model.id,
      })),
      
      removeModel: (modelId) => set((state) => ({
        models: state.models.filter(m => m.id !== modelId),
        currentModel: state.currentModel?.id === modelId ? null : state.currentModel,
        selectedModelId: state.selectedModelId === modelId ? null : state.selectedModelId,
      })),
      
      setCurrentModel: (model) => set({ currentModel: model }),
      setSelectedModelId: (modelId) => set({ selectedModelId: modelId }),
      
      updateModel: (modelId, updates) => set((state) => ({
        models: state.models.map(m => m.id === modelId ? { ...m, ...updates } : m),
        currentModel: state.currentModel?.id === modelId ? { ...state.currentModel, ...updates } : state.currentModel,
      })),
      
      setCurrentStorey: (storey) => set({ currentStorey: storey }),
      
      setSelectedCategories: (categories) => set({ selectedCategories: categories }),
      addSelectedCategory: (category) => set((state) => ({
        selectedCategories: state.selectedCategories.includes(category) 
          ? state.selectedCategories 
          : [...state.selectedCategories, category]
      })),
      removeSelectedCategory: (category) => set((state) => ({
        selectedCategories: state.selectedCategories.filter(c => c !== category)
      })),
      
      setFilteredCategories: (categories) => set({ filteredCategories: categories }),
      updateAvailableCategoriesForStorey: (categories) => set({ filteredCategories: categories }),
      
      toggleShowCategories: () => set((state) => ({ showCategories: !state.showCategories })),
      toggleShowStoreys: () => set((state) => ({ showStoreys: !state.showStoreys })),
      setTransparency: (transparency) => set({ transparency }),
      
      setStoreyVisibility: (storeyId, visible) => set((state) => {
        const newStoreyVisibility = { ...state.storeyVisibility, [storeyId]: visible };
        // Guardar automáticamente en sessionStorage
        sessionStorage.setItem('ifcStoreyVisibility', JSON.stringify(newStoreyVisibility));
        return { storeyVisibility: newStoreyVisibility };
      }),
      
      setAllStoreysVisibility: (visible) => set((state) => {
        const newStoreyVisibility = Object.keys(state.storeyVisibility).reduce((acc, storeyId) => {
          acc[storeyId] = visible;
          return acc;
        }, {} as Record<string, boolean>);
        // Guardar automáticamente en sessionStorage
        sessionStorage.setItem('ifcStoreyVisibility', JSON.stringify(newStoreyVisibility));
        return { storeyVisibility: newStoreyVisibility };
      }),
      
      loadStoreyVisibilityFromStorage: () => set((state) => {
        try {
          const stored = sessionStorage.getItem('ifcStoreyVisibility');
          if (stored) {
            const storeyVisibility = JSON.parse(stored);
            logger.log('IFC_STORE', '📁 IFCStore: Estado de visibilidad cargado desde sessionStorage:', storeyVisibility);
            return { storeyVisibility };
          }
        } catch (error) {
          console.error('❌ IFCStore: Error al cargar estado de visibilidad:', error);
        }
        return state;
      }),
      
      saveStoreyVisibilityToStorage: () => set((state) => {
        try {
          sessionStorage.setItem('ifcStoreyVisibility', JSON.stringify(state.storeyVisibility));
          logger.log('IFC_STORE', '💾 IFCStore: Estado de visibilidad guardado en sessionStorage:', state.storeyVisibility);
        } catch (error) {
          console.error('❌ IFCStore: Error al guardar estado de visibilidad:', error);
        }
        return state;
      }),
      
      setLoading: (loading) => set({ isLoading: loading }),
      setUploading: (uploading) => set({ isUploading: uploading }),
      setError: (error) => set({ error }),
      
      reset: () => set(initialState),
      
      // Métodos para verificar el estado del modelo
      hasLoadedModel: () => {
        const state = get();
        return state.currentModel !== null && state.currentModel.isLoaded;
      },
      
      getCurrentIfcModel: () => {
        const state = get();
        if (!state.currentModel) return null;
        
        // Retornar la instancia real del modelo si está disponible
        if (state.currentModel.ifcModelInstance) {
          return state.currentModel.ifcModelInstance;
        }
        
        // Fallback: retornar el modelo del store
        return state.currentModel;
      },
      
      getModelById: (modelId: string) => {
        const state = get();
        return state.models.find(m => m.id === modelId) || null;
      },
      
      getCurrentModelId: () => {
        const state = get();
        return state.currentModel?.id || null;
      },
      
      isModelVisible: (modelId: string) => {
        const state = get();
        return state.storeyVisibility[modelId] !== false; // Por defecto visible
      },
    }),
    {
      name: 'ifc-store',
    }
  )
);
